/**
 * Builds the toast shown when a status-change run completes, for both the partner settings toggle
 * and the participants bulk action.
 *
 * Extracted from the socket handler purely so this copy is testable. That handler lives in
 * useSocket, whose suite cannot currently load at all (axios ships ESM that Jest's transform
 * rejects), so anything asserted there would never run.
 */

/**
 * Includes 'Info' for the submit acknowledgement, which is deliberately not green: the run has
 * been accepted but nothing has succeeded yet. All three are existing useToaster variants.
 */
export type StatusChangeToastVariant = 'Success' | 'Danger' | 'Info';

export type StatusChangeToast = {
  variant: StatusChangeToastVariant;
  message: string;
};

type StatusChangeToastInput = {
  /** Present only for a partner toggle flip. A bulk action can span partners, so it has none. */
  partnerName?: string;
  succeeded: number;
  failed: number;
  /**
   * How many of `failed` were already excluded when the run was submitted. The event's `failed`
   * is total - succeeded, so it folds those in, and the submit toast has already explained them
   * with reasons. Subtracting leaves the worker's own failures, which are the only ones worth
   * reporting here and the only ones a retry can help. Absent means "do not adjust".
   */
  excludedAtSubmitCount?: number;
};

/**
 * The exclusion reasons the bulk endpoint reports: the four it also writes to the audit, then the
 * response-only ones describing an id that never resolved to an actionable mentee.
 *
 * `Unresolved` is retained only as a transition safety net. It was the single catch-all the API
 * sent before the reasons below existed, so a client talking to an older build still gets copy
 * with the right number in it.
 */
export type BulkExclusionReason =
  | 'Matched'
  | 'AutomaticPartner'
  | 'NotInTargetStatus'
  | 'Processing'
  | 'RegionScoped'
  | 'OutOfPartnerScope'
  | 'Deleted'
  | 'NotAMentee'
  | 'NotFound'
  | 'Duplicate'
  | 'Unresolved';

/**
 * Lifetime for the bulk status-change toasts. Longer than the shared default because this copy
 * enumerates per-reason counts and runs well over 100 characters, which is more reading than 5s
 * allows. Applies to these toasts only.
 */
export const BULK_STATUS_TOAST_MS = 9000;

export type BulkExclusionBreakdown = Partial<Record<BulkExclusionReason, number>>;

type BulkSubmitToastInput = {
  requestedCount: number;
  eligibleCount: number;
  excludedCount: number;
  excludedByReason?: BulkExclusionBreakdown;
};

/**
 * Reason phrases, written to read as a list after a count. Only Processing is transient, which is
 * why it is the only one that earns a retry prompt.
 */
const REASON_PHRASE: Record<BulkExclusionReason, string> = {
  AutomaticPartner: 'are at partners with automatic status changes',
  Matched: 'are already matched',
  NotInTargetStatus: 'are no longer in that status',
  Processing: 'are being updated by another change',
  RegionScoped: 'are managed at region level rather than by a partner',
  OutOfPartnerScope: 'belong to another partner',
  Deleted: 'have been deleted',
  NotAMentee: 'are not mentees',
  NotFound: 'no longer exist',
  Duplicate: 'were listed more than once',
  Unresolved: 'are not eligible',
};

/** Ordered so the copy is stable regardless of key order in the response. */
const REASON_ORDER: BulkExclusionReason[] = [
  'AutomaticPartner',
  'Matched',
  'NotInTargetStatus',
  'Processing',
  'RegionScoped',
  'OutOfPartnerScope',
  'Deleted',
  'NotAMentee',
  'NotFound',
  'Duplicate',
  'Unresolved',
];

const describeExclusions = (breakdown: BulkExclusionBreakdown | undefined, excludedCount: number): string => {
  const parts = REASON_ORDER.filter(reason => (breakdown?.[reason] ?? 0) > 0).map(
    reason => `${breakdown?.[reason]} ${REASON_PHRASE[reason]}`
  );

  // An older API build sends no breakdown, and a reason this client does not know yet would not
  // match any phrase. Either way the count is still true, so report that rather than nothing.
  if (!parts.length) {
    return `${excludedCount} were skipped`;
  }

  return parts.join(', ');
};

/**
 * Builds the toast shown the moment the bulk endpoint answers, before any completion event.
 *
 * Exists because the response reports how many mentees were skipped but the copy used to say
 * only that they "didn't update — try again". Most exclusion reasons are permanent, so that
 * advice was wrong as often as it was right.
 */
export function buildBulkSubmitToast({
  requestedCount,
  eligibleCount,
  excludedCount,
  excludedByReason,
}: BulkSubmitToastInput): StatusChangeToast {
  const retryable = (excludedByReason?.Processing ?? 0) > 0;
  const retryHint = retryable ? ' Try again shortly.' : '';

  if (eligibleCount === 0) {
    return {
      variant: 'Danger',
      message: `No statuses changed. ${describeExclusions(excludedByReason, excludedCount)}.${retryHint}`,
    };
  }

  if (excludedCount === 0) {
    return { variant: 'Info', message: 'Status change started' };
  }

  return {
    variant: 'Info',
    message: `Status change started for ${eligibleCount} of ${requestedCount} mentees. ${describeExclusions(
      excludedByReason,
      excludedCount
    )}.${retryHint}`,
  };
}

export function buildStatusChangeToast({
  partnerName,
  succeeded,
  failed,
  excludedAtSubmitCount,
}: StatusChangeToastInput): StatusChangeToast {
  // Submit-time exclusions were already reported, with reasons, by buildBulkSubmitToast. Counting
  // them again here told the admin 49 statuses failed when the worker had failed nobody.
  const workerFailed = Math.max(failed - (excludedAtSubmitCount ?? 0), 0);
  // Named on both outcomes, not just the failure. An admin can flip any partner and may have
  // several tabs open, and the success case is precisely where knowing which partner matters.
  const scope = partnerName ? ` for ${partnerName}` : '';

  if (workerFailed > 0) {
    return { variant: 'Danger', message: `${workerFailed} user statuses didn't update${scope} — try again` };
  }

  return { variant: 'Success', message: `${succeeded} user statuses changed successfully${scope}` };
}
